import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { PushNotifications } from "@capacitor/push-notifications";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { post } from "../http/api";

const PUSH_ENABLED =
  process.env.REACT_APP_PUSH_ENABLED === "true";

const getNotificationRoute = (data = {}) => {
  if (data.type === 'profile_visit') {
    return `/home?profileVisit=${encodeURIComponent(data.notification_id || '')}`;
  }
  if (data.route) return data.route;
  if (data.type === "message" || data.type === "message_summary") {
    return "/home?tab=messages";
  }
  if (data.sender_username) {
    return `/user/${data.sender_username}`;
  }
  return "/home";
};

export const unregisterMobilePush = async () => {
  if (!Capacitor.isNativePlatform() || !PUSH_ENABLED) return;

  const token = sessionStorage.getItem("mobile_push_token");

  try {
    if (token) {
      await post("notifications/devices/unregister", { token });
    }
    await PushNotifications.unregister();
  } catch (error) {
    console.error("PUSH UNREGISTER ERROR:", error);
  } finally {
    sessionStorage.removeItem("mobile_push_token");
  }
};

export default function MobileRuntime() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let registering = false;
    const listenerHandles = [];
    const visualViewport = window.visualViewport;
    const updateViewportHeight = () => {
      const viewportHeight = visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty(
        "--native-viewport-height",
        `${Math.round(viewportHeight)}px`
      );
    };

    const registerForPush = async () => {
      if (
        !PUSH_ENABLED ||
        registering ||
        !sessionStorage.getItem("token")
      ) {
        return;
      }
      registering = true;

      try {
        let permission = await PushNotifications.checkPermissions();

        if (permission.receive === "prompt") {
          permission = await PushNotifications.requestPermissions();
        }

        if (permission.receive === "granted") {
          await PushNotifications.register();
        }
      } catch (error) {
        console.error("PUSH REGISTRATION ERROR:", error);
      } finally {
        registering = false;
      }
    };

    const configureNativeShell = async () => {
      try {
        document.documentElement.classList.add("native-app");
        updateViewportHeight();
        await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
        await Keyboard.setScroll({ isDisabled: false });
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setStyle({ style: Style.Light });

        if (Capacitor.getPlatform() === "android") {
          await StatusBar.setBackgroundColor({ color: "#020617" });
          if (PUSH_ENABLED) {
            await PushNotifications.createChannel({
              id: "carrerup_activity",
              name: "Activité CarrerUp",
              description: "Profils, publications, réseau et messages",
              importance: 5,
              visibility: 1,
              vibration: true
            });
          }
        }

        await SplashScreen.hide();
      } catch (error) {
        console.warn("NATIVE SHELL CONFIGURATION:", error);
      }
    };

    configureNativeShell();
    registerForPush();

    const authenticatedHandler = () => registerForPush();
    window.addEventListener("carrerup:authenticated", authenticatedHandler);
    window.addEventListener("resize", updateViewportHeight);
    visualViewport?.addEventListener("resize", updateViewportHeight);

    Promise.all([
      ...(PUSH_ENABLED ? [
        PushNotifications.addListener("registration", async ({ value }) => {
          try {
            sessionStorage.setItem("mobile_push_token", value);
            await post("notifications/devices", {
              token: value,
              platform: Capacitor.getPlatform()
            });
          } catch (error) {
            console.error("DEVICE TOKEN SAVE ERROR:", error);
          }
        }),
        PushNotifications.addListener("registrationError", (error) => {
          console.error("NATIVE PUSH ERROR:", error);
        }),
        PushNotifications.addListener(
          "pushNotificationActionPerformed",
          ({ notification }) => {
            navigate(getNotificationRoute(notification?.data));
          }
        )
      ] : []),
      Keyboard.addListener("keyboardWillShow", ({ keyboardHeight }) => {
        document.documentElement.classList.add("native-keyboard-open");
        document.documentElement.style.setProperty(
          "--native-keyboard-height",
          `${keyboardHeight || 0}px`
        );

        updateViewportHeight();
      }),
      Keyboard.addListener("keyboardDidShow", () => {
        updateViewportHeight();
        window.setTimeout(() => {
          const activeElement = document.activeElement;
          if (
            activeElement &&
            /^(INPUT|TEXTAREA|SELECT)$/.test(activeElement.tagName)
          ) {
            activeElement.scrollIntoView({
              block: "center",
              inline: "nearest",
              behavior: "smooth"
            });
          }
        }, 80);
      }),
      Keyboard.addListener("keyboardWillHide", () => {
        document.documentElement.classList.remove("native-keyboard-open");
        document.documentElement.style.setProperty(
          "--native-keyboard-height",
          "0px"
        );
      }),
      Keyboard.addListener("keyboardDidHide", updateViewportHeight),
      CapacitorApp.addListener("backButton", ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          CapacitorApp.exitApp();
        }
      }),
      CapacitorApp.addListener("appUrlOpen", ({ url }) => {
        try {
          const parsedUrl = new URL(url);
          navigate(`${parsedUrl.pathname}${parsedUrl.search}`);
        } catch (error) {
          console.warn("INVALID APP URL:", url);
        }
      })
    ]).then((handles) => listenerHandles.push(...handles));

    return () => {
      document.documentElement.classList.remove("native-app");
      document.documentElement.classList.remove("native-keyboard-open");
      document.documentElement.style.removeProperty("--native-keyboard-height");
      document.documentElement.style.removeProperty("--native-viewport-height");
      window.removeEventListener("carrerup:authenticated", authenticatedHandler);
      window.removeEventListener("resize", updateViewportHeight);
      visualViewport?.removeEventListener("resize", updateViewportHeight);
      listenerHandles.forEach((handle) => handle.remove());
    };
  }, [navigate]);

  return null;
}
